<template lang="pug">
div
  v-card-text
    div(style="position:relative; height:320px")
      BarChartGenerator(:chart-data="chartData" :chart-options="chartOptions" :chart-id="id")
</template>


<script>
import { Bar as BarChartGenerator } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

import { mapActions, mapGetters } from 'vuex'
import chartsUtils from '@/utils/chartsUtils'
import { fmtMoneyMM } from '@/utils/customVueFilters'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
)

// Notación de dinero unificada (ver utils/customVueFilters fmtMoneyMM).
const fmtCompact = fmtMoneyMM

export default {
  components: {
    BarChartGenerator
  },
  data() {
    return {
      id: this._uid.toString(),
      chartData: {
        labels: [],
        datasets: [
        ]
      },
    }
  },
  computed: {
    ...mapGetters('app_config', ['currentBusiness']),
    isSimpleMode() {
      return this.currentBusiness && this.currentBusiness.catalog_mode === 'simplificado'
    },
    chartOptions() {
      // En modo simplificado (servicios) no se usa "SKU": se habla de productos/servicios.
      const itemLabel = this.isSimpleMode ? 'productos' : 'SKU'
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: { usePointStyle: true, boxWidth: 8 }
          },
          tooltip: {
            callbacks: {
              label: function(context) {
                const extra = context && context.dataset['extra'] && context.dataset['extra'][context.dataIndex]
                const skuCount = extra ? extra['sku_count'] : 0
                const communeCount = extra ? extra['commune_count'] : 0
                const val = fmtCompact(context.raw)
                return extra
                  ? `${context.dataset.label}: ${val} (${skuCount} ${itemLabel} · ${communeCount} comunas)`
                  : `${context.dataset.label}: ${val}`
              },
            }
          }
        },
        layout: { padding: { bottom: 4 } },
        scales: {
          y: {
            beginAtZero: true,
            ticks: { callback: (v) => fmtCompact(v) },
            grid: { color: 'rgba(0,0,0,0.05)' }
          },
          x: {
            grid: { display: false },
            // El eje X muestra un grupo por CASO. Los nombres pueden ser largos:
            // los mostramos horizontales y truncados (el nombre completo va en el tooltip).
            ticks: {
              display: true,
              autoSkip: false,
              maxRotation: 0,
              minRotation: 0,
              font: { size: 11 },
              // Nombres de caso largos: los envolvemos en varias líneas (máx 3) para
              // que el eje sea legible sin rotar. El nombre completo va en el tooltip.
              callback: function(value) {
                const label = String(this.getLabelForValue(value) || '')
                const words = label.split(' ')
                const lines = []
                let line = ''
                for (const w of words) {
                  if ((line + ' ' + w).trim().length > 18) {
                    if (line) lines.push(line.trim())
                    line = w
                  } else {
                    line = (line + ' ' + w).trim()
                  }
                }
                if (line) lines.push(line.trim())
                if (lines.length > 3) {
                  return [...lines.slice(0, 2), lines.slice(2).join(' ').slice(0, 17) + '…']
                }
                return lines
              }
            }
          }
        }
      }
    }
  },
  methods: {
    ...mapActions('commercial_projection', ['getProjectionComparisonChart']),
    async fetchData() {
      const { data, error } = await this.getProjectionComparisonChart()

      if (!error) {
        this.chartData = {
          labels: data.labels,
          datasets: data.datasets.map((dataset, index) => ({
            label: dataset.label,
            data: dataset.data,
            extra: dataset.extra,
            backgroundColor: chartsUtils.colors[index % chartsUtils.colors.length],
            hoverBackgroundColor: chartsUtils.colors[index % chartsUtils.colors.length],
            borderColor: chartsUtils.colors[index % chartsUtils.colors.length],
            borderRadius: 6,
            maxBarThickness: 48
          }))
        }
      }
    },
  },
  watch: {
    chartBy() {
      this.fetchData()
    }
  },
  created() {
    this.fetchData()
  }
}
</script>
